import React, { useMemo, useState } from 'react'
import { Body, Button, Grid, PageHeader, PageSection, SubNav } from '@superhi/design'

import { API } from '../../api'
import LibraryPostArticleCard from '../../components/LibraryPostArticleCard'
import LibraryPostVideoCard from '../../components/LibraryPostVideoCard'
import Page from '../../components/Page'
import { buildPath } from '../../routing/utils'
import { PageConfig } from '../../routing/types'

import { Props } from './data'
import ROUTE from './route'

const VS_1 = { small: 32, medium: 40, large: 56, xlarge: 56 }
const GRID_1_CELL_SPAN_1: React.ComponentProps<typeof Grid.Cell>['span'] = {
  small: 'full',
  medium: 4,
  large: 4,
}

export const TYPES_LANGUAGE: Record<API.LibraryPostType, string> = {
  ADVICE: 'Advice',
  ARTICLE: 'Articles',
  INTERVIEW: 'Interviews',
  TUTORIAL: 'Tutorials',
}

const PER_PAGE = 12

const Topic: PageConfig<Props> = ({ id, color, title, filters, slug, posts = [] }) => {
  const [offset, setOffset] = useState(1)

  const typeOptions = useMemo(
    () => [
      ...Object.entries(TYPES_LANGUAGE).map(([id, value]) => ({
        id: id,
        display: value,
        href: buildPath('topics-view-types-view', {
          params: { topicSlug: slug, typeSlug: id.toLowerCase() },
        }),
      })),
      {
        id: 'ALL',
        display: 'All types',
        href: buildPath('topics-view', { params: { topicSlug: slug } }),
      },
    ],
    [slug],
  )

  const totalPostsNum = posts.length
  const filteredPosts = useMemo(() => posts.slice(0, PER_PAGE * offset), [posts, offset])
  const filteredPostsNum = filteredPosts.length
  const hasMorePosts = filteredPostsNum < totalPostsNum
  const showMore = () => setOffset((currentOffset) => currentOffset + 1)

  return (
    <Page
      title={title}
      description="Resources and advice on how to make, build, learn and grow your creative career."
      key={id}
    >
      <PageHeader backgroundVersion={color} title={title} compact />

      <SubNav currentId={filters.type} items={typeOptions} />

      <PageSection footer={hasMorePosts && <Button onClick={showMore}>See more</Button>}>
        <Grid rowGap={VS_1}>
          {filteredPosts.length > 0 ? (
            filteredPosts.map((post): React.ReactElement => {
              switch (post.__typename) {
                case 'LibraryPostArticle': {
                  return (
                    <Grid.Cell key={post.slug} span={GRID_1_CELL_SPAN_1}>
                      <LibraryPostArticleCard
                        {...post}
                        headingLevel="2"
                        noType={filters.type !== 'ALL'}
                        imageLazy
                      />
                    </Grid.Cell>
                  )
                }
                case 'LibraryPostVideo': {
                  return (
                    <Grid.Cell key={post.slug} span={GRID_1_CELL_SPAN_1}>
                      <LibraryPostVideoCard
                        {...post}
                        headingLevel="2"
                        noType={filters.type !== 'ALL'}
                        imageLazy
                      />
                    </Grid.Cell>
                  )
                }
              }
            })
          ) : (
            <Grid.Cell span="full">
              <Body level="3">No posts yet.</Body>
            </Grid.Cell>
          )}
        </Grid>
      </PageSection>
    </Page>
  )
}

Topic.route = ROUTE

export { getStaticPaths, getStaticProps } from './data'
export default Topic
